/**
 * Course Mode — searching the timetable instead of the building list.
 *
 * A student does not know that MATH 1500 A02 meets in Armes 200; they know the
 * string printed on their registration. So the search box can be switched to
 * take that string and hand back the room, which is then an ordinary
 * destination — everything downstream (the pin, Find Route, the deep link) is
 * the room's, not the course's. This file is the whole translation layer.
 *
 * The data is `courses.json` in the snapshot, built by
 * scripts/build-courses.js from UMapsTools' Aurora export. Like every other
 * file in that folder it is a POINT-IN-TIME snapshot: a term's rooms do move,
 * and nothing here notices. It is fetched only when the toggle is switched on.
 *
 * ⚠️ A section is expanded to ONE ROW PER ROOM (`courseRows`), not one row per
 * section. 78 of the 2551 placed sections meet in two rooms — a lecture in one
 * hall and its lab somewhere else — and a single row for those would have to
 * pick one silently. Every row here means exactly one place to walk to.
 *
 * A section with NO room is kept and says so, rather than being filtered out:
 * an online section that answers "no results" reads as a broken search.
 */

const DAY_NAMES = { M: 'Mon', T: 'Tue', W: 'Wed', R: 'Thu', F: 'Fri', S: 'Sat', U: 'Sun' };

/** "MW" → "Mon Wed". Aurora's letters are what a timetable prints, but R for
 *  Thursday is jargon the map has no reason to repeat. */
export function dayNames(days) {
  return String(days || '')
    .toUpperCase()
    .split('')
    .map(d => DAY_NAMES[d])
    .filter(Boolean)
    .join(' ');
}

/** "14:30" → "2:30 PM". */
export function clockTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return '';
  const h = Number(m[1]);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${ampm}`;
}

/** "Mon Wed · 2:30 PM – 3:45 PM" — one meeting, as a row subtitle. */
export function meetingLabel(m) {
  if (!m) return '';
  const days = dayNames(m.days);
  const start = clockTime(m.start);
  const end = clockTime(m.end);
  const time = start && end ? `${start} – ${end}` : start || '';
  return [days, time].filter(Boolean).join(' · ');
}

/**
 * The loaded file → what the search works over. Sections are pre-lowercased
 * into their match fields here so a keystroke costs a comparison, not a parse:
 * ~3900 sections are scored on every character typed.
 */
export function buildCourseIndex(json) {
  if (!json || !Array.isArray(json.sections)) return null;
  const rooms = new Map((json.rooms || []).map(r => [r.id, r]));
  const sections = json.sections.map(s => {
    const code = String(s.code || '');
    const [subject = '', number = ''] = code.split(/\s+/);
    const compact = code.replace(/\s+/g, '').toLowerCase();
    const section = String(s.section || '');
    return {
      ...s,
      subject: subject.toLowerCase(),
      number: number.toLowerCase(),
      compact,                          // "math1500"
      full: compact + section.toLowerCase(), // "math1500a02"
      sectionLower: section.toLowerCase(),
      titleLower: String(s.title || '').toLowerCase(),
      instructorLower: String(s.instructor || '').toLowerCase(),
    };
  });
  return { termName: json.termName || '', term: json.term || '', rooms, sections };
}

/** How well one section answers one typed token, or 0 for "it doesn't". */
function tokenScore(s, t) {
  if (s.compact === t || s.full === t) return 120;
  if (s.crn === t) return 110;
  if (s.number === t) return 100;
  if (s.subject === t) return 95;
  if (s.full.startsWith(t)) return 80;
  if (s.compact.startsWith(t)) return 70;
  if (s.sectionLower === t) return 65;
  if (s.number.startsWith(t)) return 50;
  if (s.subject.startsWith(t)) return 45;
  if (s.sectionLower.startsWith(t)) return 30;
  if (s.titleLower.includes(t)) return 15;
  if (s.instructorLower.includes(t)) return 10;
  return 0;
}

/**
 * Sections matching every typed token. "math" lists the subject, "math 1500"
 * narrows to the course, "math1500a02" (no spaces, as it is printed) to the one
 * section — and the instructor's name works too, for the student who knows only
 * that.
 *
 * Placed sections rank above room-less ones at equal score: the room is the
 * question being asked, so a section that has one is the better answer.
 */
export function searchCourses(index, query, limit = 12) {
  if (!index) return [];
  const tokens = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const scored = [];
  for (const s of index.sections) {
    let total = 0, ok = true;
    for (const t of tokens) {
      const sc = tokenScore(s, t);
      if (!sc) { ok = false; break; }
      total += sc;
    }
    if (ok) scored.push({ s, score: total });
  }
  scored.sort((a, b) =>
    (a.s.meetings.length ? 0 : 1) - (b.s.meetings.length ? 0 : 1) ||
    b.score - a.score ||
    a.s.code.localeCompare(b.s.code) ||
    a.s.section.localeCompare(b.s.section)
  );
  return scored.slice(0, limit).map(x => x.s);
}

/**
 * Search results as pickable rows: one per room a section meets in, plus one
 * room-less row for a section that has none. `limit` counts ROWS, so a search
 * that turns up two-room sections does not quietly show fewer courses.
 */
export function courseRows(index, query, limit = 12) {
  const rows = [];
  // Ask for the row limit in sections; a section rarely expands, so this is
  // enough to fill the list without scoring the file twice.
  for (const s of searchCourses(index, query, limit)) {
    const byRoom = new Map();
    for (const m of s.meetings) {
      if (!byRoom.has(m.room)) byRoom.set(m.room, []);
      byRoom.get(m.room).push(m);
    }
    if (byRoom.size === 0) {
      rows.push(courseRow(index, s, null, []));
    } else {
      for (const [roomId, meetings] of byRoom) rows.push(courseRow(index, s, roomId, meetings));
    }
    if (rows.length >= limit) break;
  }
  return rows.slice(0, limit);
}

function courseRow(index, s, roomId, meetings) {
  const room = roomId ? index.rooms.get(roomId) : null;
  return {
    key: `${s.crn}::${roomId || 'none'}`,
    code: s.code,
    section: s.section,
    title: s.title,
    crn: s.crn,
    instructor: s.instructor,
    roomId,
    slug: room?.slug || null,
    roomNumber: room?.room || null,
    roomLabel: room ? `${room.name} ${room.room}` : '',
    meetings,
    when: meetings.map(meetingLabel).filter(Boolean).join(' · '),
  };
}

/** What the search box shows once a row is picked: "MATH 1500 A02". */
export function courseLabel(row) {
  if (!row) return '';
  return [row.code, row.section].filter(Boolean).join(' ');
}

/**
 * A picked row → the roomIndex entry the rest of the app routes to, or null
 * when the course's room is not on the map.
 *
 * Null is a real answer, not a failure: 3 of the 277 timetabled rooms have no
 * door node in the snapshot, and every online section has no room at all. The
 * row says so instead of offering a destination that cannot be walked to.
 */
export function courseRoomEntry(roomIndex, row) {
  if (!roomIndex || !row?.slug || !row.roomNumber) return null;
  const want = String(row.roomNumber).toLowerCase();
  return roomIndex.find(e => e.building === row.slug && String(e.roomNumber).toLowerCase() === want) || null;
}
